<template>
    <div class="search-result">
        <div class="search-result__column" data-column="title" data-title="Title">
            <router-link :to="link" class="company">
                <img
                    v-if="resource.creator.avatar"
                    :src="`/images/avatar/${resource.creator.id}`"
                    class="avatar avatar--small"
                    :title="resource.creator.name" :alt="`${resource.creator.name}'s avatar`"
                    width="32" height="32"
                />
                <img v-else src="//placehold.it/32x32" class="avatar avatar--small" width="32" height="32" alt=""/>
                <span v-html="resource.name" class="name"/>
            </router-link>
        </div>
        <div
            class="search-result__column"
            data-column="category"
            data-title="Category"
        >
            <span v-text="resource.category"/>
        </div>
        <div class="search-result__column" data-column="sector" data-title="Sector">
            <span class="sector" v-if="sector">{{ sector.name }}</span>
        </div>
        <div
            class="search-result__column"
            data-column="created"
            data-title="Created"
        >
            <span v-html="resource.created_at"/>
        </div>
        <div v-if="resource.found_highlights" data-column="highlights" class="search-result__column">
            <HighlightResult
                v-for="highlight in resource.found_highlights"
                :resource="highlight"
                :key="highlight.key"
            />
        </div>
        <div v-if="resource.attachment_highlights && resource.attachment_highlights.length > 0" data-column="documents" class="search-result__column">
            <div class="highlighted-result">
                <span class="highlighted-result__title">AI Overview</span>
                <div class="highlighted-result__content">
                    <ul class="ai-docs">
                        <li v-for="attachment_highlight in resource.attachment_highlights">
                            <AIOverview :resource="attachment_highlight" />
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { parse, format } from 'date-fns';
import { mapState } from 'vuex';
import HighlightResult from '@components/Search/HighlightResult.vue';
import AIOverview from '@components/Search/AIOverview.vue';

export default {
    name: 'DocumentSearchResult',
    components: {
        AIOverview,
        HighlightResult},
    props: {
        resource: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapState({
            user: state => state.auth.user,
        }),
        formattedTitle() {
            if(this.resource.project_name === this.resource.company_name) {
                return this.resource.project_name;
            }

            if(!this.resource.project_name) {
                return this.resource.company_name;
            }

            return `${ this.resource.project_name } (${ this.resource.company_name })`;
        },
        sector() {
            if(!this.resource.sectors || !this.resource.sectors.length) {
                return '';
            }

            if(!this.user.subscribed || !this.user.subscribed.length) {
                return this.resource.sectors[0];
            }

            //Filter row sectors to ones the user is subscribed to
            let subscribed = this.user.subscribed.map(sector => sector.id),
                sectors = this.resource.sectors.filter(sector => subscribed.indexOf(sector.id) > -1);

            //Return first subscribed sector, or first row sector if none are subscribed to
            return sectors.length ? sectors[0] : this.resource.sectors[0];
        },
        link() {
            return {
                name: (this.resource.status === 2) ? 'view-document' : 'preview-document',
                params: {insight_id: this.resource.id},
            };
        },
        date() {
            return this.resource.created_at ? format(parse(this.resource.created_at), 'dd/MM/yy') : 'Unknown';
        },
    },
}
</script>
<style lang="scss" scoped>
@import "~@/theme/_variables.scss";

.search-result {
    display: flex;
    flex-wrap: wrap;
    row-gap: 1rem;
    align-items: center;

    .search-result__column {
        &[data-column="title"] {
            flex: 1 1 auto;
            max-width: calc(((100% - 20rem) - 24rem) - 10.2rem);
            font-weight: bold;
            padding-right: 1rem;

            .company {
                color: #000000;
                display: flex;
                align-items: center;

                .flag {
                    margin-right: 1rem;
                    max-width: 2.4rem;
                    height: auto;
                    flex: 0 0 auto;
                    position: relative;
                }

                .name {
                    max-width: calc(100% - 3.8rem);
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    font-size: 1.4rem;
                }
            }
        }

        &[data-column="category"] {
            flex: 0 0 20rem;
        }

        &[data-column="sector"] {
            flex: 0 0 24rem;
            display: flex;
            flex-wrap: wrap;
            gap: .5rem;

            .sector {
                margin-right: 0;
                margin-bottom: 0;
            }
        }

        &[data-column="created"] {
            flex: 0 0 10rem;
            text-align: right;
        }

        &[data-column="documents"],
        &[data-column="highlights"] {
            flex: 1 0 100%;
            width: 100%;
        }
    }

    @media screen and (max-width: 991px) {
        row-gap: 2rem;
        align-items: flex-start;
        justify-content: space-between;

        .search-result__column {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-start;
            align-self: flex-start;
            gap: 1rem;

            &:before {
                content: attr(data-title);
                color: $text;
                font-size: 1.4rem;
                font-weight: normal;
                line-height: 1;
                display: block;
            }

            &[data-column="title"] {
                flex: 1 0 100%;
                max-width: 100%;
                padding-right: 0;

                a {
                    width: 100%;
                }
            }

            &[data-column="category"],
            &[data-column="sector"],
            &[data-column="created"] {
                flex: 0 0 calc(33% - .6rem);
                gap: 1rem;

                span:not(.sector) {
                    line-height: 2rem;
                }

                .sector {
                    display: inline-flex;
                }
            }
        }
    }

    @media screen and (max-width: 575px) {
        .search-result__column {
            &[data-column="category"] {
                flex: 0 0 calc(((100% - 7rem) / 2) - .4rem);
            }

            &[data-column="sector"] {
                flex: 0 0 calc(((100% - 7rem) / 2) - .4rem);
            }

            &[data-column="created"] {
                flex: 0 0 7rem;
            }
        }
    }
}
</style>
